import React, { memo } from "react";
import { Handle, Position } from "react-flow-renderer";
import { Typography, Chip } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { ContentCopy, Delete, ArrowForwardIos, Loop, CheckCircle } from "@mui/icons-material";
import { useNodeStorage } from "../../../stores/useNodeStorage";

function ForNode({ data, isConnectable, id }) {
  const theme = useTheme();
  const storageItems = useNodeStorage();

  const arrayVariable = data?.arrayVariable || "${array}";
  const itemVariable = data?.itemVariable || "item";
  const indexVariable = data?.indexVariable || "index";

  const truncateText = (text, maxLength = 20) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  return (
    <div
      style={{
        backgroundColor: theme.palette.background.paper,
        padding: theme.spacing(2),
        borderRadius: theme.shape.borderRadius,
        border: `2px solid #00acc1`,
        boxShadow: `0 4px 8px rgba(0, 0, 0, 0.15)`,
        position: "relative",
        transition: "all 0.2s ease",
        width: "auto",
        minWidth: "220px",
        maxWidth: "280px",
        boxSizing: "border-box",
      }}
    >
      {/* Handle de entrada */}
      <Handle
        type="target"
        position={Position.Left}
        style={{
          background: "transparent",
          width: "28px",
          height: "28px",
          left: "-14px",
          top: "50%",
          transform: "translateY(-50%)",
          cursor: "crosshair",
          border: "none",
        }}
        isConnectable={isConnectable}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              background: `linear-gradient(145deg, #00acc1 0%, #00838f 100%)`,
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              boxShadow: theme.shadows[2],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "none",
            }}
          >
            <ArrowForwardIos
              style={{
                color: "#fff",
                width: "12px",
                height: "12px",
                transform: "rotate(180deg)",
              }}
            />
          </div>
        </div>
      </Handle>

      {/* Botoes de acao */}
      <div
        style={{
          display: "flex",
          position: "absolute",
          right: theme.spacing(1),
          top: theme.spacing(1),
          gap: theme.spacing(1),
          cursor: "pointer",
        }}
      >
        <ContentCopy
          onClick={() => {
            storageItems.setNodesStorage(id);
            storageItems.setAct("duplicate");
          }}
          style={{
            width: "16px",
            height: "16px",
            color: theme.palette.primary.main,
          }}
        />
        <Delete
          onClick={() => {
            storageItems.setNodesStorage(id);
            storageItems.setAct("delete");
          }}
          style={{
            width: "16px",
            height: "16px",
            color: theme.palette.error.main,
          }}
        />
      </div>

      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: theme.spacing(1),
          marginBottom: theme.spacing(1.5),
        }}
      >
        <Loop
          style={{
            width: "18px",
            height: "18px",
            color: "#00acc1",
          }}
        />
        <div
          style={{
            color: theme.palette.text.primary,
            fontSize: "15px",
            fontWeight: 500,
          }}
        >
          For (Loop)
        </div>
      </div>

      {/* Array */}
      <div
        style={{
          backgroundColor: theme.palette.background.default,
          borderRadius: theme.shape.borderRadius,
          padding: theme.spacing(1.5),
          border: `1px solid ${theme.palette.divider}`,
          marginBottom: theme.spacing(1),
        }}
      >
        <Typography
          variant="caption"
          style={{
            color: theme.palette.text.secondary,
            fontWeight: 600,
            display: "block",
            marginBottom: 4,
          }}
        >
          Array:
        </Typography>
        <Chip
          size="small"
          label={truncateText(arrayVariable)}
          title={arrayVariable}
          style={{
            backgroundColor: "rgba(0, 172, 193, 0.15)",
            color: "#00acc1",
            fontWeight: 500,
            fontSize: "11px",
          }}
        />
      </div>

      {/* Variáveis */}
      <div
        style={{
          display: "flex",
          gap: theme.spacing(1),
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            backgroundColor: "rgba(0, 172, 193, 0.08)",
            borderRadius: 4,
            padding: "4px 8px",
            border: `1px solid rgba(0, 172, 193, 0.2)`,
          }}
        >
          <Typography
            variant="caption"
            style={{ color: theme.palette.text.secondary, fontSize: "10px" }}
          >
            Item: <strong style={{ color: "#00acc1" }}>${"{" + itemVariable + "}"}</strong>
          </Typography>
        </div>
        <div
          style={{
            backgroundColor: "rgba(0, 172, 193, 0.08)",
            borderRadius: 4,
            padding: "4px 8px",
            border: `1px solid rgba(0, 172, 193, 0.2)`,
          }}
        >
          <Typography
            variant="caption"
            style={{ color: theme.palette.text.secondary, fontSize: "10px" }}
          >
            Índice: <strong style={{ color: "#00acc1" }}>${"{" + indexVariable + "}"}</strong>
          </Typography>
        </div>
      </div>

      {/* Labels das saídas */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: theme.spacing(1.5),
          gap: theme.spacing(2),
          paddingRight: theme.spacing(1),
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <Loop style={{ color: "#00acc1", width: 14, height: 14 }} />
          <Typography variant="caption" style={{ color: "#00acc1", fontWeight: 500 }}>
            Loop
          </Typography>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <CheckCircle style={{ color: "#4caf50", width: 14, height: 14 }} />
          <Typography variant="caption" style={{ color: "#4caf50", fontWeight: 500 }}>
            Fim
          </Typography>
        </div>
      </div>

      {/* Handle de saída LOOP */}
      <Handle
        type="source"
        position={Position.Right}
        id="loop"
        style={{
          background: "transparent",
          width: "28px",
          height: "28px",
          right: "-14px",
          top: "35%",
          cursor: "crosshair",
          border: "none",
        }}
        isConnectable={isConnectable}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              background: `linear-gradient(145deg, #00acc1 0%, #00838f 100%)`,
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              boxShadow: theme.shadows[2],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "none",
            }}
          >
            <Loop
              style={{
                color: "#fff",
                width: "12px",
                height: "12px",
              }}
            />
          </div>
        </div>
      </Handle>

      {/* Handle de saída DONE */}
      <Handle
        type="source"
        position={Position.Right}
        id="done"
        style={{
          background: "transparent",
          width: "28px",
          height: "28px",
          right: "-14px",
          top: "75%",
          cursor: "crosshair",
          border: "none",
        }}
        isConnectable={isConnectable}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              background: `linear-gradient(145deg, #4caf50 0%, #388e3c 100%)`,
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              boxShadow: theme.shadows[2],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "none",
            }}
          >
            <CheckCircle
              style={{
                color: "#fff",
                width: "12px",
                height: "12px",
              }}
            />
          </div>
        </div>
      </Handle>
    </div>
  );
}

export default memo(ForNode);
